/* ==========================================================================
   Supply6 — cart freebie engine  (assets/s6-freebies.js)   v2.1 · 8 Oct 2026
   ==========================================================================

   WHAT THIS FILE DOES
   -------------------
   It keeps the free (₹0) gift lines in the cart correct. It watches the cart,
   works out which gifts the shopper has earned, and adds or removes those
   lines. It replaces the freebie behaviour the old GoKwik cart drawer gave us.

   THERE ARE NO PRODUCT IDS IN THIS FILE
   -------------------------------------
   Every rule lives in the theme editor:

     Shopify admin -> Online Store -> Themes -> Customize -> Theme settings
       -> "Supply6 Freebies"

   Each "Rule" slot there says: WHEN these products (optionally only these
   pack sizes) are in the cart, GIVE this ₹0 gift product, optionally only
   between a start and an end date. snippets/js-variables.liquid turns those
   settings into window.themeVariables.settings.s6Freebies and this file
   reads them.

   ONE VARIANT, TWO PAGES, DIFFERENT GIFTS (v2.1, 8 Oct 2026)
   --------------------------------------------------------
   A rule can also say "only for lines tagged ..." or "skip lines tagged ...".
   The tag is a line-item PROPERTY on the trigger line, written as name=value
   (or just name, for any value). The Welcome Kit page (assets/wk.js) adds the
   360 60-pack with _s6_source=welcome-kit; the normal 360 page adds the very
   same variant with no tag. So the Welcome Kit rules REQUIRE that tag (they
   never fire from the 360 PDP) and the regular 360 rule EXCLUDES it (a kit
   line never also earns the regular gift). Same SKU, same stock, two paths.

   The cart-VALUE gifts ("spend ₹899, get a wafer") are a separate, older
   engine in assets/custom.js (addFreeProduct), configured under Theme
   settings -> Milestone Bar -> Milestone Base Free Gift. This file does not
   add those, but it does keep them honest (see below).

   THE FOUR HARD RULES (v2)
   ------------------------
   These hold on every page, after every cart change, whoever made it:

     1. NOTHING FREE WITHOUT SOMETHING PAID. If the cart has no paid product
        line (a line with a price above ₹0 that is not itself a gift and not
        a ₹99 "claim" item), every gift line and every claim line is removed
        and checkout is blocked. A cart cannot leave the site holding only
        freebies.
     2. A GIFT IS ONLY EVER EARNED. A rule gift stays only while a trigger
        product is in the cart. A milestone gift stays only while the cart
        value is at or above its milestone. A ₹0 gift-looking line that no
        rule and no milestone currently grants (added from the gift's own
        product page, an old cart, a stale tab) is removed.
     3. ONE PER FAMILY, QUANTITY ONE. Never two shakers / two bottles; every
        gift line is forced back to quantity 1.
     4. THE CART IS ALWAYS RE-READ. This file never trusts a cart object
        handed to it by another script; before deciding anything it fetches
        /cart.js afresh, and every pass is capped by a timeout so one hung
        request can never switch the engine off for the rest of the page.

   ONE GIFT PER FAMILY, EVER
   -------------------------
   The shop has several ₹0 versions of the same gift (Steel Pro Shaker Teal,
   Steel Pro Shaker Snow, ...). Theme settings -> Supply6 Freebies -> "One per
   family" lists words like "shaker, bottle". Any gift line whose product
   title contains one of those words belongs to that family, and the cart is
   never allowed to hold more than ONE line of a family, whoever added it.

   THE GIFT ARRIVES WITH THE PRODUCT, NOT AFTER IT
   -----------------------------------------------
   The gift line is added in the SAME /cart/add.js request as the product the
   shopper tapped, so the cart updates once and the drawer draws once.

   REMOVING A GIFT, AND ADDING IT BACK
   -----------------------------------
   Every gift row has a small bin button. Tapping it remembers "this gift was
   dismissed" in sessionStorage (this tab only) and removes the line. The gift
   then reappears as an "Add back" row while the cart still qualifies.

   WHERE EACH PIECE OF CART LOGIC LIVES
   ------------------------------------
   sections/mini-cart.liquid   the drawer's HTML and CSS. No JavaScript in it.
   snippets/s6-cart-line.liquid one row of the drawer (gift pill + bin).
   sections/main-cart.liquid   the /cart PAGE (original theme markup). This
                               file dresses its gift rows at runtime because
                               that section has no gift markup of its own.
   snippets/js-variables.liquid turns Theme settings into `s6Freebies`.
   snippets/gokwik-checkout.liquid the checkout button; rendered disabled
                               when the cart holds no paid line. This file
                               re-applies that state after every change.
   assets/custom.js            everything else about the cart (jQuery), and
                               the cart-VALUE milestone gifts (addFreeProduct).
   assets/s6-freebies.js       this file.

   DEBUGGING
   ---------
   Theme settings -> Supply6 Freebies -> Developer -> "Log ... to console",
   or in the browser console: S6Freebies.rules(), S6Freebies.plan(cart),
   S6Freebies.families(), S6Freebies.reconcile(), S6Freebies.allowed().
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- CONFIG */
  var CONFIG = {
    enabled: true,
    familyKeywords: ['shaker', 'bottle'],
    rules: [],
    propertyName: '_s6_freebie',
    debug: false
  };

  var S6_ENGINE_ENABLED = true;

  // How long one reconcile pass may take before the engine gives up on it and
  // carries on with the next one. One hung request must never freeze the
  // engine for the rest of the page.
  var PASS_TIMEOUT_MS = 10000;
  var REFRESH_TIMEOUT_MS = 6000;

  // Loop breaker: if the cart keeps changing back and forth (another script
  // fighting us), stop writing for a while instead of ping-ponging forever.
  var LOOP_WINDOW_MS = 10000;
  var LOOP_MAX_WRITES = 6;
  var LOOP_BACKOFF_MS = 5000;

  /* ------------------------------------------------------------- UTILITIES */

  function log() {
    if (!CONFIG.debug || !window.console) return;
    console.log.apply(console, ['[s6-freebies]'].concat([].slice.call(arguments)));
  }

  function warn() {
    if (!window.console) return;
    console.warn.apply(console, ['[s6-freebies]'].concat([].slice.call(arguments)));
  }

  function toId(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  function titleOf(item) {
    return String((item && (item.product_title || item.title)) || '');
  }

  function cleanTitle(title) {
    return String(title || '').replace(/^[^A-Za-z0-9]+/, '').trim();
  }

  function giftLabel(title) {
    return 'Free ' + cleanTitle(title);
  }

  // "name=value" or "name" -> { name, value|null }; blank -> null. Case-insensitive.
  function parseTag(str) {
    if (typeof str !== 'string') return null;
    var t = str.trim();
    if (!t) return null;
    var eq = t.indexOf('=');
    if (eq === -1) return { name: t.toLowerCase(), value: null };
    var name = t.slice(0, eq).trim().toLowerCase();
    var value = t.slice(eq + 1).trim().toLowerCase();
    if (!name) return null;
    return { name: name, value: value || null };
  }

  function ruleInWindow(rule, now) {
    if (rule.start) {
      var s = Date.parse(rule.start);
      if (!isNaN(s) && now < s) return false;
    }
    if (rule.end) {
      var e = Date.parse(rule.end);
      if (!isNaN(e) && now > e) return false;
    }
    return true;
  }

  // Resolve with the promise's value, or with null after `ms`, never reject.
  function withTimeout(promise, ms, tag) {
    return new Promise(function (resolve) {
      var done = false;
      var t = setTimeout(function () {
        if (done) return;
        done = true;
        warn(tag + ' timed out after ' + ms + 'ms; carrying on');
        resolve(null);
      }, ms);
      Promise.resolve(promise).then(function (v) {
        if (done) return;
        done = true; clearTimeout(t); resolve(v);
      }, function (err) {
        if (done) return;
        done = true; clearTimeout(t); log(tag + ' failed', err); resolve(null);
      });
    });
  }

  function isCartPage() {
    var s = window.themeVariables && window.themeVariables.settings;
    if (s && s.pageType === 'cart') return true;
    return /^\/cart\/?$/.test(window.location.pathname || '');
  }

  /* ------------------------------------------------ THEME-SETTINGS INTAKE */

  function applyThemeSettings() {
    var s = window.themeVariables &&
            window.themeVariables.settings &&
            window.themeVariables.settings.s6Freebies;

    if (!s) {
      warn('no s6Freebies settings found on the page; no product rules will run');
      return;
    }

    if (typeof s.debug === 'boolean') CONFIG.debug = s.debug;
    if (typeof s.enabled === 'boolean') S6_ENGINE_ENABLED = s.enabled;

    if (typeof s.familyKeywords === 'string') {
      var words = s.familyKeywords.split(',').map(function (w) {
        return w.trim().toLowerCase();
      }).filter(Boolean);
      if (words.length) CONFIG.familyKeywords = words;
    }

    var rules = [];
    (s.rules || []).forEach(function (r, i) {
      if (!r) return;
      var gift = r.gift || {};
      var giftVid = toId(gift.variantId);
      var rule = {
        id: r.id || ('rule' + (i + 1)),
        name: r.name || ('Rule ' + (i + 1)),
        enabled: r.enabled !== false,
        start: (typeof r.start === 'string' && r.start.trim()) ? r.start.trim() : null,
        end: (typeof r.end === 'string' && r.end.trim()) ? r.end.trim() : null,
        serverActive: r.serverActive !== false,
        triggerVariantIds: (r.triggerVariantIds || []).map(toId).filter(function (v) { return v !== null; }),
        triggerProductIds: (r.triggerProductIds || []).map(toId).filter(function (v) { return v !== null; }),
        requireTag: parseTag(r.requireTag),
        excludeTag: parseTag(r.excludeTag),
        gift: giftVid ? { variantId: giftVid, title: gift.title || 'Free gift', price: toId(gift.price) } : null
      };

      if (!rule.gift) {
        if (rule.enabled && (rule.triggerVariantIds.length || rule.triggerProductIds.length)) {
          warn('rule "' + rule.name + '" is switched on but has no ₹0 gift product; ignored');
        }
        return;
      }
      if (rule.gift.price !== null && rule.gift.price !== 0) {
        warn('rule "' + rule.name + '" gift is not ₹0; ignored');
        return;
      }
      if (!rule.triggerVariantIds.length && !rule.triggerProductIds.length) {
        if (rule.enabled) warn('rule "' + rule.name + '" has no trigger products; ignored');
        return;
      }
      rules.push(rule);
    });
    CONFIG.rules = rules;
  }

  applyThemeSettings();

  /* ------------------------------------------------- GIFTS AND FAMILIES */

  function giftKey(variantId) { return 'gift-' + toId(variantId); }

  function giftsByKey() {
    var out = {};
    CONFIG.rules.forEach(function (rule) {
      if (!rule.gift) return;
      var key = giftKey(rule.gift.variantId);
      if (!out[key]) out[key] = { key: key, variantId: rule.gift.variantId, title: rule.gift.title, label: giftLabel(rule.gift.title) };
    });
    return out;
  }

  function giftForKey(key) { return giftsByKey()[key] || null; }

  function familyOfTitle(title) {
    var t = String(title || '').toLowerCase();
    for (var i = 0; i < CONFIG.familyKeywords.length; i++) {
      if (t.indexOf(CONFIG.familyKeywords[i]) !== -1) return CONFIG.familyKeywords[i];
    }
    return null;
  }

  function familyOfGiftKey(key) {
    var g = giftForKey(key);
    return g ? familyOfTitle(g.title) : null;
  }

  /* ----------------------------------------------- WHICH LINES ARE WHAT */

  function isFreebieLine(item) {
    return !!(item && item.properties && item.properties[CONFIG.propertyName]);
  }

  function isLegacyGokwikFreebieLine(item) {
    var p = (item && item.properties) || {};
    return !!(p._gokwik_freebie || p._gkfc);
  }

  function isMilestoneGiftLine(item) {
    var p = (item && item.properties) || {};
    return p._type === 'free_gift' || !!p._milestone_gift_level || !!p._free_gift_of;
  }

  function isAnyGiftLine(item) {
    return isFreebieLine(item) || isLegacyGokwikFreebieLine(item) || isMilestoneGiftLine(item);
  }

  // ₹99 "claim" strip items (custom.js). Paid, but only meaningful next to a
  // real product, so they are not counted as "something paid".
  function isClaimLine(item) {
    var p = (item && item.properties) || {};
    return p._claim === true || p._claim === 'true';
  }

  // A line that looks like a gift: carries a gift property, or is ₹0 and is
  // titled / handled like one. A ₹0 line that looks like nothing we know is
  // left alone.
  function isGiftLike(item) {
    if (!item) return false;
    if (isAnyGiftLine(item)) return true;
    if (item.price !== 0) return false;
    var t = titleOf(item);
    if (/^\s*🎁/.test(t)) return true;                 // 🎁
    if (/gift|freebie/i.test(String(item.handle || ''))) return true;
    return !!familyOfTitle(t);
  }

  // The thing the whole engine hinges on: a real, paid product line.
  function isPaidLine(item) {
    return !!item && item.price > 0 && !isAnyGiftLine(item) && !isClaimLine(item);
  }

  function hasPaidLine(cart) {
    return !!(cart && cart.items && cart.items.some(isPaidLine));
  }

  function checkoutAllowed(cart) {
    // Unknown cart (engine not booted yet): do not interfere.
    if (!cart || !cart.items) return true;
    return hasPaidLine(cart);
  }

  function lineFamily(item) {
    if (!item) return null;
    if (item.price !== 0 && !isAnyGiftLine(item)) return null;
    return familyOfTitle(titleOf(item));
  }

  function freebieKeyForLine(item) {
    if (!item) return null;
    var props = item.properties || {};
    if (props[CONFIG.propertyName]) return giftKey(item.variant_id);
    if (isMilestoneGiftLine(item)) return null;
    if (!isLegacyGokwikFreebieLine(item) && item.price !== 0) return null;
    var key = giftKey(item.variant_id);
    return giftForKey(key) ? key : null;
  }

  /* ---------------------------------------------------------- RULE LOGIC */

  function ruleLive(rule, now) {
    if (rule.enabled === false) return false;
    if (rule.serverActive === false) return false;
    return ruleInWindow(rule, now);
  }

  // Does this line carry the tag? Properties come back from /cart.js as an
  // object; on an add payload they may be an object too (see readAddItems).
  function lineHasTag(props, tag) {
    if (!tag || !props || typeof props !== 'object') return false;
    var keys = Object.keys(props);
    for (var i = 0; i < keys.length; i++) {
      if (String(keys[i]).trim().toLowerCase() !== tag.name) continue;
      if (tag.value === null) return true;
      if (String(props[keys[i]] === null || props[keys[i]] === undefined ? '' : props[keys[i]]).trim().toLowerCase() === tag.value) return true;
    }
    return false;
  }

  // The rule's tag filters, applied to one trigger line's properties.
  function ruleAllowsLine(rule, props) {
    if (rule.requireTag && !lineHasTag(props, rule.requireTag)) return false;
    if (rule.excludeTag && lineHasTag(props, rule.excludeTag)) return false;
    return true;
  }

  function ruleHits(rule, productId, variantId) {
    if (productId !== null && rule.triggerProductIds.indexOf(productId) !== -1) return true;
    if (variantId !== null && rule.triggerVariantIds.indexOf(variantId) !== -1) return true;
    return false;
  }

  function activeRuleIds(cart) {
    var now = Date.now();
    var ids = {};
    (cart.items || []).forEach(function (item) {
      if (isAnyGiftLine(item)) return;          // gifts never trigger gifts
      if (item.price === 0) return;             // nor does anything free
      var pid = toId(item.product_id);
      var vid = toId(item.variant_id);
      CONFIG.rules.forEach(function (rule) {
        if (ids[rule.id]) return;
        if (!ruleLive(rule, now)) return;
        if (!ruleAllowsLine(rule, item.properties)) return;
        if (ruleHits(rule, pid, vid)) ids[rule.id] = true;
      });
    });
    return ids;
  }

  function desiredGifts(activeIds) {
    var wanted = {};
    CONFIG.rules.forEach(function (rule) {
      if (activeIds[rule.id] && rule.gift) wanted[giftKey(rule.gift.variantId)] = true;
    });
    return wanted;
  }

  function coveredFamilies(activeIds) {
    var fams = {};
    Object.keys(desiredGifts(activeIds)).forEach(function (key) {
      var f = familyOfGiftKey(key);
      if (f) fams[f] = true;
    });
    return fams;
  }

  /* --------------------------------------------------- DISMISSED GIFTS ---- */
  var DISMISS_KEY = 's6_freebies_dismissed';
  var dismissedCache = {};

  function readDismissed() {
    try {
      var raw = window.sessionStorage.getItem(DISMISS_KEY);
      var obj = raw ? JSON.parse(raw) : null;
      dismissedCache = (obj && typeof obj === 'object') ? obj : {};
    } catch (e) { /* storage unavailable: keep the in-memory copy */ }
    return dismissedCache;
  }

  function writeDismissed(map) {
    dismissedCache = map || {};
    try { window.sessionStorage.setItem(DISMISS_KEY, JSON.stringify(dismissedCache)); }
    catch (e) { /* not persisted; in-memory copy still works for this page */ }
  }

  function isDismissed(key) { return !!(key && dismissedCache[key]); }

  function setDismissed(key, on) {
    if (!key) return;
    var map = readDismissed();
    if (on) { map[key] = 1; } else { delete map[key]; }
    writeDismissed(map);
  }

  readDismissed();

  /* ------------------------------------------- MILESTONE GIFTS (custom.js) */

  function milestoneGift(n) {
    var s = window.themeVariables && window.themeVariables.settings;
    if (!s) return null;
    var p = s['freeProduct_' + n];
    var vid = p && p.variants && p.variants[0] && toId(p.variants[0].id);
    if (!vid) return null;
    return {
      key: 'milestone-' + n,
      n: n,
      variantId: vid,
      title: p.title || 'Free gift',
      label: giftLabel(p.title || 'Free gift'),
      family: familyOfTitle(p.title),
      thresholdPaise: (parseInt(s['freeProduct_milestone_' + n] || '0', 10) || 0) * 100
    };
  }

  function milestoneGifts() {
    var out = [];
    [1, 2].forEach(function (n) { var g = milestoneGift(n); if (g) out.push(g); });
    return out;
  }

  function milestoneGiftByKey(key) {
    var found = null;
    milestoneGifts().forEach(function (g) { if (g.key === key) found = g; });
    return found;
  }

  function milestoneGiftByVariant(variantId) {
    var vid = toId(variantId);
    var found = null;
    milestoneGifts().forEach(function (g) { if (g.variantId === vid) found = g; });
    return found;
  }

  function milestoneVariantForFamily(family) {
    var found = null;
    milestoneGifts().forEach(function (g) {
      if (found === null && g.family && g.family === family) found = g.variantId;
    });
    return found;
  }

  // Same basis custom.js uses for the milestone bar: the pre-discount total.
  function milestoneBasis(cart) {
    return cart.original_total_price || cart.total_price || 0;
  }

  function milestoneEarned(cart, key, covered) {
    var g = milestoneGiftByKey(key);
    if (!g || !g.thresholdPaise) return false;
    if (g.family && covered[g.family]) return false;
    if (!hasPaidLine(cart)) return false;
    return milestoneBasis(cart) >= g.thresholdPaise;
  }

  /* --------------------------------- KEEP THE MILESTONE ENGINE HONEST */
  var lastCovered = {};

  function patchMilestoneEngine() {
    var original = window.addFreeProduct;
    if (typeof original !== 'function' || original.__s6Patched) return false;

    var patched = function (event, cart) {
      var settings = window.themeVariables && window.themeVariables.settings;
      var stash = {};
      var liveCart = (cart && cart.items) ? cart : cachedCart;
      var covered = {};
      var familyLines = {};
      var paid = liveCart && liveCart.items ? hasPaidLine(liveCart) : true;

      if (liveCart && liveCart.items) {
        covered = coveredFamilies(activeRuleIds(liveCart));
        liveCart.items.forEach(function (item) {
          var f = lineFamily(item);
          if (!f) return;
          if (!familyLines[f]) familyLines[f] = [];
          familyLines[f].push(item);
        });
      }
      lastCovered = covered;

      if (settings) {
        milestoneGifts().forEach(function (g) {
          var field = 'freeProduct_' + g.n;
          var hide = false;
          var why = '';

          if (!paid) { hide = true; why = 'no paid product in cart'; }

          if (!hide && g.family && covered[g.family]) { hide = true; why = 'family covered by a rule'; }

          if (!hide && g.family && familyLines[g.family]) {
            familyLines[g.family].forEach(function (item) {
              if (isMilestoneGiftLine(item) && toId(item.variant_id) === g.variantId) return;
              hide = true; why = 'family already in cart';
            });
          }

          if (!hide && isDismissed(g.key)) { hide = true; why = 'dismissed by shopper'; }

          if (hide) {
            stash[field] = settings[field];
            settings[field] = null;
            log('milestone gift', g.n, 'hidden for this pass:', why);
          }
        });
      }

      try {
        return original.apply(this, arguments);
      } finally {
        Object.keys(stash).forEach(function (field) { settings[field] = stash[field]; });
      }
    };

    patched.__s6Patched = true;
    window.addFreeProduct = patched;
    log('milestone engine patched');
    return true;
  }

  /* ----------------------------------------------- DRAWER PRESENTATION ---- */

  var STYLE_ID = 's6-freebies-style';

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var css = [
      // Gift rows never get a +/- box or a text "Remove" link, anywhere.
      '.s6-freebie-line .quantity-selector,',
      '.s6-freebie-line quantity-selector,',
      '.s6-freebie-line .line-item__remove-button,',
      '.s6-freebie-line [data-remove-link],',
      '.s6-freebie-line input[name^="updates"] { display: none !important; }',
      '.line-item--gift .quantity-selector { display: none !important; }',
      // Runtime-drawn pill + bin for the /cart page (the drawer prints its own).
      '.s6-freebie-line .s6-runtime-gift { display: inline-flex; align-items: center; gap: 8px; }',
      '.s6-freebie-line .s6-runtime-gift .line-item__gift-qty { display: inline-block; font-size: 11px; font-weight: 700; line-height: 1.2; color: #014344; background: #D3FA99; border-radius: 999px; padding: 4px 10px; white-space: nowrap; }',
      '.s6-freebie-line .s6-runtime-gift .line-item__gift-remove { border: 0; background: none; color: #929393; font-size: 12px; text-decoration: underline; cursor: pointer; padding: 6px; }',
      // Checkout blocked because the cart holds nothing paid.
      '.s6-checkout-blocked { opacity: .45 !important; cursor: not-allowed !important; }',
      // Small notice shown when a blocked checkout is tapped.
      '.s6-toast { position: fixed; left: 50%; bottom: 96px; transform: translateX(-50%); z-index: 2147483000; background: #014344; color: #fff; font: 600 13px/1.3 inherit; padding: 10px 14px; border-radius: 999px; box-shadow: 0 6px 20px rgba(0,0,0,.25); max-width: 92vw; text-align: center; pointer-events: none; }'
    ].join('\n');
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(el);
  }

  var toastTimer = null;
  function toast(text) {
    try {
      var old = document.querySelector('.s6-toast');
      if (old && old.parentNode) old.parentNode.removeChild(old);
      var el = document.createElement('div');
      el.className = 's6-toast';
      el.setAttribute('role', 'status');
      el.appendChild(document.createTextNode(text));
      document.body.appendChild(el);
      clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 2600);
    } catch (e) { /* cosmetic only */ }
  }

  // Every rendered cart row on the page: the drawer's rows and, on /cart,
  // the cart page's rows. Deduplicated.
  function lineItemNodes() {
    var out = [];
    function push(el) { if (out.indexOf(el) === -1) out.push(el); }
    Array.prototype.forEach.call(document.querySelectorAll('.mini-cart-line-items line-item'), push);
    if (isCartPage()) {
      Array.prototype.forEach.call(document.querySelectorAll('line-item, .line-item'), function (el) {
        // Only real rows: something that carries a quantity or a remove control.
        if (el.querySelector && (el.querySelector('.line-item__quantity, .quantity-selector, quantity-selector, [data-remove-link], .line-item__remove-button, input[name^="updates"]'))) push(el);
      });
    }
    return out;
  }

  function variantFromLinks(el) {
    var a = el.querySelector && el.querySelector('a[href*="variant="]');
    if (!a) return null;
    var m = /[?&]variant=(\d+)/.exec(a.getAttribute('href') || '');
    return m ? toId(m[1]) : null;
  }

  function matchRowToItem(el, cart, byKey, byVariant, pageRows) {
    var item = byKey[el.getAttribute('data-line-key')];
    if (!item) item = byVariant[el.getAttribute('data-variant-id')];
    if (!item) item = byVariant[variantFromLinks(el)];
    if (!item && pageRows) {
      // /cart page renders cart.items once, in order.
      var idx = pageRows.indexOf(el);
      if (idx !== -1 && cart.items[idx]) item = cart.items[idx];
    }
    return item || null;
  }

  function dressRuntimeGiftRow(el, item) {
    if (el.querySelector('.line-item__gift-qty')) return;   // drawer printed its own
    if (el.querySelector('.s6-runtime-gift')) return;
    var host = el.querySelector('.line-item__quantity') || el;
    var wrap = document.createElement('span');
    wrap.className = 's6-runtime-gift';
    var pill = document.createElement('span');
    pill.className = 'line-item__gift-qty';
    pill.appendChild(document.createTextNode('Free · Qty 1'));
    var bin = document.createElement('button');
    bin.type = 'button';
    bin.className = 'line-item__gift-remove';
    bin.setAttribute('data-s6-gift-remove', '1');
    bin.setAttribute('aria-label', 'Remove free gift');
    bin.appendChild(document.createTextNode('Remove'));
    wrap.appendChild(pill);
    wrap.appendChild(bin);
    host.appendChild(wrap);
  }

  function decorateLines(cart) {
    if (!cart || !cart.items) return;
    var nodes = lineItemNodes();
    if (!nodes.length) return;

    var byKey = {};
    var byVariant = {};
    cart.items.forEach(function (item) {
      byKey[item.key] = item;
      if (!byVariant[item.variant_id]) byVariant[item.variant_id] = item;
    });

    var onCartPage = isCartPage();
    var pageRows = onCartPage ? nodes.filter(function (el) { return !el.closest || !el.closest('.mini-cart-line-items'); }) : null;

    nodes.forEach(function (el) {
      var item = matchRowToItem(el, cart, byKey, byVariant, pageRows);
      if (!item) return;
      var key = freebieKeyForLine(item);
      el.setAttribute('data-s6-line-key', item.key);
      if (!el.getAttribute('data-variant-id')) el.setAttribute('data-variant-id', item.variant_id);
      if (key) {
        el.classList.add('line-item--gift', 's6-freebie-line');
        el.setAttribute('data-s6-freebie', key);
        el.removeAttribute('data-s6-milestone-gift');
      } else if (isMilestoneGiftLine(item) || isGiftLike(item)) {
        el.classList.add('line-item--gift', 's6-freebie-line');
        el.removeAttribute('data-s6-freebie');
        el.setAttribute('data-s6-milestone-gift', item.variant_id);
      } else {
        el.classList.remove('s6-freebie-line');
        el.removeAttribute('data-s6-freebie');
        el.removeAttribute('data-s6-milestone-gift');
        return;
      }
      if (onCartPage) dressRuntimeGiftRow(el, item);
    });
  }

  /* --------------------------------------------------- PROGRESS BAR TWEAKS */
  function markCoveredMilestonesUnlocked(covered) {
    var nodes = document.querySelectorAll('.reward-bar__segment, .shipiing-progress-track .check-point');
    Array.prototype.forEach.call(nodes, function (node) {
      var text = (node.textContent || '').toLowerCase();
      var fam = null;
      CONFIG.familyKeywords.forEach(function (w) { if (fam === null && text.indexOf(w) !== -1) fam = w; });
      if (!fam) return;
      if (covered[fam]) {
        node.classList.add('is-unlocked', 'has_magenta', 's6-freebie-unlocked');
      } else if (node.classList.contains('s6-freebie-unlocked')) {
        node.classList.remove('is-unlocked', 'has_magenta', 's6-freebie-unlocked');
      }
    });
  }

  /* ------------------------------------------------------ CHECKOUT GUARD */
  /*
     The checkout button is rendered by Liquid with `disabled` when the cart
     holds nothing paid, but the drawer is patched in place by other scripts
     and the button can come back enabled. So this file (a) marks every
     checkout control blocked/unblocked after each pass and after every
     re-render, and (b) intercepts the click itself in the capture phase, so
     an inline onclick (GoKwik's) never even fires on a freebie-only cart.
  */
  var CHECKOUT_SEL = [
    '.gokwik-checkout-button',
    '.gokwik-checkout button',
    '#gokwik-buy-now',
    '.checkout-button',
    'button[name="checkout"]',
    'input[type="submit"][name="checkout"]',
    'a[href*="/checkout"]'
  ].join(', ');

  function applyCheckoutGuard(cart) {
    var ok = checkoutAllowed(cart);
    var nodes = document.querySelectorAll(CHECKOUT_SEL);
    Array.prototype.forEach.call(nodes, function (node) {
      if (!ok) {
        node.setAttribute('data-s6-blocked', '1');
        node.classList.add('s6-checkout-blocked');
        node.setAttribute('aria-disabled', 'true');
        if ('disabled' in node && node.tagName !== 'A') node.disabled = true;
      } else if (node.getAttribute('data-s6-blocked')) {
        node.removeAttribute('data-s6-blocked');
        node.classList.remove('s6-checkout-blocked');
        node.removeAttribute('aria-disabled');
        if ('disabled' in node && node.tagName !== 'A') node.disabled = false;
      }
    });
  }

  function blockedMessage() {
    return 'Add a product to your cart to check out - free items can’t be ordered on their own.';
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || t.nodeType !== 1 || !t.closest) return;
    var btn = t.closest(CHECKOUT_SEL);
    if (!btn) return;
    if (btn.id === 'gokwik-buy-now' && !isCartPage()) return;   // PDP buy-now adds a paid product itself
    if (checkoutAllowed(cachedCart)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();
    toast(blockedMessage());
    reconcile();
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    var isCheckout = form.querySelector('[name="checkout"]') ||
                     /\/checkout/.test(form.getAttribute('action') || '');
    if (!isCheckout) return;
    if (checkoutAllowed(cachedCart)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    toast(blockedMessage());
    reconcile();
  }, true);

  /* ------------------------------------------------------- RECONCILIATION  */

  var chain = Promise.resolve();   // serialises every pass
  var busy  = false;              // true while a pass is running (informational)
  var lastCart = null;
  var cachedCart = null;
  var lastEarned = {};
  var s6OwnWrite = false;         // true only while WE are talking to the cart
  var writeStamps = [];           // loop breaker
  var backoffUntil = 0;

  function s6Post(url, payload, tag) {
    s6OwnWrite = true;
    var request;
    try {
      request = fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
    } finally {
      s6OwnWrite = false;
    }
    return request.then(function (r) {
      if (!r.ok) log(tag + ' failed', r.status);
      return r;
    }).catch(function (e) { log(tag + ' error', e); });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' }, cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { if (cart && cart.items) cachedCart = cart; return cart; });
  }

  function addItems(items) {
    if (!items.length) return Promise.resolve();
    return s6Post('/cart/add.js', { items: items }, 'add');
  }

  function giftItem(key) {
    var g = giftForKey(key);
    var props = {};
    props[CONFIG.propertyName] = key;
    return { id: g.variantId, quantity: 1, properties: props };
  }

  function updateLines(updates) {
    if (!Object.keys(updates).length) return Promise.resolve();
    return s6Post('/cart/update.js', { updates: updates }, 'update');
  }

  function setLineQuantity(key, quantity) {
    var updates = {};
    updates[key] = quantity;
    return updateLines(updates);
  }

  function planFor(cart) {
    var items = cart.items || [];
    var activeIds = activeRuleIds(cart);
    var earned    = desiredGifts(activeIds);
    var covered   = coveredFamilies(activeIds);

    var wanted = {};
    Object.keys(earned).forEach(function (key) {
      if (!isDismissed(key)) wanted[key] = true;
    });

    var toAdd = [], toRemove = [], toFix = [], seen = {};

    function markRemove(item, why) {
      if (toRemove.indexOf(item.key) !== -1) return;
      toRemove.push(item.key);
      log('remove', why, titleOf(item), item.properties);
    }
    function markFix(item) {
      if (item.quantity !== 1 && toRemove.indexOf(item.key) === -1 && toFix.indexOf(item.key) === -1) toFix.push(item.key);
    }
    function removed(item) { return toRemove.indexOf(item.key) !== -1; }

    // 0. NOTHING FREE WITHOUT SOMETHING PAID.
    if (!hasPaidLine(cart)) {
      items.forEach(function (it) {
        if (isGiftLike(it)) markRemove(it, 'no paid product in cart');
        else if (isClaimLine(it)) markRemove(it, 'claim item without a paid product');
      });
      if (!toRemove.length) return null;
      return { toAdd: [], toRemove: toRemove, toFix: [] };
    }

    // 1. OUR gift lines (and legacy / bare ₹0 lines of a configured gift),
    //    grouped by gift. Keep one line per wanted gift, drop the rest.
    var groups = {};
    items.forEach(function (item) {
      var key = freebieKeyForLine(item);
      if (!key) return;
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    Object.keys(groups).forEach(function (key) {
      var lines = groups[key];
      var keeper = null;
      lines.forEach(function (it) { if (!keeper && isFreebieLine(it)) keeper = it; });
      if (!keeper) keeper = lines[0];
      var keep = !!wanted[key] && !!giftForKey(key);
      lines.forEach(function (it) {
        if (!keep || it !== keeper) { markRemove(it, keep ? 'duplicate gift' : 'gift not earned'); return; }
        seen[key] = true;
        markFix(it);
      });
    });
    Object.keys(wanted).forEach(function (key) { if (!seen[key]) toAdd.push(key); });

    // 2. ONE PER FAMILY.
    var families = {};
    items.forEach(function (item) {
      if (removed(item)) return;
      var f = lineFamily(item);
      if (!f) return;
      if (!families[f]) families[f] = [];
      families[f].push(item);
    });

    Object.keys(families).forEach(function (f) {
      var lines = families[f];
      var keeper = null;

      if (covered[f]) {
        lines.forEach(function (it) { if (!keeper && isFreebieLine(it) && seen[freebieKeyForLine(it)]) keeper = it; });
      } else {
        var current = milestoneVariantForFamily(f);
        lines.forEach(function (it) {
          if (!keeper && isMilestoneGiftLine(it) && current !== null && toId(it.variant_id) === current) keeper = it;
        });
      }

      lines.forEach(function (it) {
        if (it === keeper) { markFix(it); return; }
        if (isFreebieLine(it) && seen[freebieKeyForLine(it)] && keeper === null) {
          markFix(it); return;
        }
        markRemove(it, 'second ' + f + ' in cart');
      });
    });

    // 3. MILESTONE GIFTS: only the configured variant, only while the cart is
    //    at or above its milestone. (custom.js adds them; this makes sure the
    //    removal side cannot be missed.)
    var basis = milestoneBasis(cart);
    items.forEach(function (it) {
      if (removed(it) || !isMilestoneGiftLine(it)) return;
      var g = milestoneGiftByVariant(it.variant_id);
      if (!g) { markRemove(it, 'milestone gift no longer configured'); return; }
      if (g.thresholdPaise && basis < g.thresholdPaise) { markRemove(it, 'cart below the ' + (g.thresholdPaise / 100) + ' milestone'); return; }
      markFix(it);
    });

    // 4. ANY OTHER gift-looking ₹0 line nobody currently grants goes; and
    //    every gift-looking line that stays is quantity 1.
    items.forEach(function (it) {
      if (removed(it) || !isGiftLike(it)) return;
      if (freebieKeyForLine(it) || isMilestoneGiftLine(it)) { markFix(it); return; }
      markRemove(it, 'free item no rule or milestone grants');
    });

    if (!toAdd.length && !toRemove.length && !toFix.length) return null;
    return { toAdd: toAdd, toRemove: toRemove, toFix: toFix };
  }

  function loopBreakerAllows() {
    var now = Date.now();
    if (now < backoffUntil) return false;
    writeStamps = writeStamps.filter(function (t) { return now - t < LOOP_WINDOW_MS; });
    if (writeStamps.length >= LOOP_MAX_WRITES) {
      backoffUntil = now + LOOP_BACKOFF_MS;
      warn('cart keeps changing back and forth; pausing writes for ' + (LOOP_BACKOFF_MS / 1000) + 's');
      return false;
    }
    writeStamps.push(now);
    return true;
  }

  function applyPlan(plan) {
    var updates = {};
    plan.toRemove.forEach(function (key) { updates[key] = 0; });
    plan.toFix.forEach(function (key) { updates[key] = 1; });
    return updateLines(updates).then(function () {
      return addItems(plan.toAdd.map(giftItem));
    });
  }

  function refreshDrawer() {
    if (typeof window.themeRefreshCart === 'function') {
      try { return Promise.resolve(window.themeRefreshCart(false)); } catch (e) { log('themeRefreshCart threw', e); }
    }
    return fetchCart().then(function (cart) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart, openMiniCart: false } }));
    });
  }

  function settle(cart) {
    if (!cart || !cart.items) return;
    var activeIds = activeRuleIds(cart);
    var covered   = coveredFamilies(activeIds);
    lastCart = cart;
    cachedCart = cart;
    lastEarned = desiredGifts(activeIds);
    lastCovered = covered;
    forgetStaleDismissals(cart, lastEarned, covered);
    markCoveredMilestonesUnlocked(covered);
    decorateLines(cart);
    renderGiftBackSlot(cart, lastEarned, covered);
    applyCheckoutGuard(cart);
  }

  // One pass: read the cart fresh, fix what is wrong, redraw.
  function runPass() {
    return fetchCart().then(function (cart) {
      if (!cart || !cart.items) return false;
      settle(cart);
      var plan = planFor(cart);
      if (!plan) { log('cart already correct'); return false; }
      if (!loopBreakerAllows()) return false;
      log('applying', plan);
      return applyPlan(plan).then(function () { return true; });
    }).then(function (changed) {
      if (!changed) return null;
      return withTimeout(refreshDrawer(), REFRESH_TIMEOUT_MS, 'drawer refresh').then(function () {
        // Re-read after the theme has redrawn (custom.js may have written too).
        return fetchCart().then(settle);
      });
    });
  }

  // The cart argument is accepted for compatibility but deliberately ignored:
  // every pass reads /cart.js itself (hard rule 4).
  function reconcile(/* cartFromEvent */) {
    chain = chain.then(function () {
      busy = true;
      return withTimeout(runPass(), PASS_TIMEOUT_MS, 'reconcile pass')
        .then(function () { busy = false; reapplySoon(); });
    });
    return chain;
  }

  /* ------------------------------------------- "YOUR FREE GIFT" ADD-BACK ROW */

  var SLOT_CLASS = 's6-giftback';

  function forgetStaleDismissals(cart, earned, covered) {
    var map = readDismissed();
    var changed = false;
    Object.keys(map).forEach(function (key) {
      var stillEarned = key.indexOf('milestone-') === 0
        ? milestoneEarned(cart, key, covered)
        : !!earned[key];
      if (!stillEarned) { delete map[key]; changed = true; }
    });
    if (changed) writeDismissed(map);
  }

  function giftBackOffers(cart, earned, covered) {
    var offers = [];
    if (!hasPaidLine(cart)) return offers;
    Object.keys(earned).forEach(function (key) {
      if (!isDismissed(key)) return;
      var g = giftForKey(key);
      if (g) offers.push({ key: key, label: g.label });
    });
    milestoneGifts().forEach(function (g) {
      if (!isDismissed(g.key)) return;
      if (!milestoneEarned(cart, g.key, covered)) return;
      offers.push({ key: g.key, label: g.label });
    });
    return offers;
  }

  function renderGiftBackSlot(cart, earned, covered) {
    var lists = document.querySelectorAll('.mini-cart-line-items');
    if (!lists.length || !cart) return;

    var offers = giftBackOffers(cart, earned || {}, covered || {});
    var signature = offers.map(function (o) { return o.key; }).join('|');

    Array.prototype.forEach.call(lists, function (list) {
      var old = list.querySelectorAll('.' + SLOT_CLASS);
      if (list.__s6Slot === signature && old.length === offers.length) return;
      list.__s6Slot = signature;

      Array.prototype.forEach.call(old, function (n) { if (n.parentNode) n.parentNode.removeChild(n); });

      offers.forEach(function (offer) {
        var row = document.createElement('div');
        row.className = SLOT_CLASS;
        var text = document.createElement('div');
        text.className = 's6-giftback__text';
        var strong = document.createElement('b');
        strong.appendChild(document.createTextNode('Your free gift'));
        text.appendChild(strong);
        text.appendChild(document.createTextNode(offer.label + ' — yours free with this order'));
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 's6-giftback__add';
        btn.setAttribute('data-s6-gift-add', offer.key);
        btn.appendChild(document.createTextNode('Add back'));
        row.appendChild(text);
        row.appendChild(btn);
        list.appendChild(row);
      });
    });
  }

  function reapplyDecorations() {
    if (!lastCart) return;
    injectStyles();
    decorateLines(lastCart);
    markCoveredMilestonesUnlocked(lastCovered);
    renderGiftBackSlot(lastCart, lastEarned, lastCovered);
    applyCheckoutGuard(lastCart);
  }

  var reapplyTimers = [];
  function reapplySoon() {
    reapplyTimers.forEach(function (t) { clearTimeout(t); });
    reapplyTimers = [120, 400, 900, 1800].map(function (ms) {
      return setTimeout(reapplyDecorations, ms);
    });
    if (window.requestAnimationFrame) window.requestAnimationFrame(reapplyDecorations);
    else reapplyDecorations();
  }

  /* ------------------------------------------------ REMOVE / ADD-BACK CLICKS */

  function giftKeyForRow(row) {
    var key = row.getAttribute('data-s6-freebie');
    if (key) return giftForKey(key) ? key : giftKey(row.getAttribute('data-variant-id'));

    var match = null;
    var vid = toId(row.getAttribute('data-s6-milestone-gift'));
    if (vid !== null) {
      milestoneGifts().forEach(function (g) { if (g.variantId === vid) match = g.key; });
      if (match) return match;
    }
    vid = toId(row.getAttribute('data-variant-id'));
    if (vid === null) return null;
    if (giftForKey(giftKey(vid))) return giftKey(vid);
    milestoneGifts().forEach(function (g) { if (g.variantId === vid) match = g.key; });
    return match;
  }

  function handleGiftRemove(btn) {
    var row = btn.closest ? btn.closest('line-item, .line-item') : null;
    if (!row) return;

    setDismissed(giftKeyForRow(row), true);

    var lineKey = row.getAttribute('data-line-key') || row.getAttribute('data-s6-line-key');
    btn.disabled = true;

    chain = chain.then(function () {
      if (lineKey) return setLineQuantity(lineKey, 0);
      var link = row.querySelector('[data-remove-link]');
      if (link) link.click();
      return null;
    }).then(function () {
      if (lineKey) return withTimeout(refreshDrawer(), REFRESH_TIMEOUT_MS, 'drawer refresh');
      return null;
    }).then(function () {
      return fetchCart().then(settle);
    }).then(function () {
      reapplySoon();
    }).catch(function (e) { log('gift remove failed', e); });

    return chain;
  }

  function handleGiftAddBack(key, btn) {
    if (!key) return;
    setDismissed(key, false);
    if (btn) btn.disabled = true;

    var variantId = null;
    var props = null;

    var g = giftForKey(key);
    if (g) {
      variantId = g.variantId;
      props = {};
      props[CONFIG.propertyName] = key;
    } else {
      var m = milestoneGiftByKey(key);
      if (m) {
        variantId = m.variantId;
        props = { '_type': 'free_gift' };
      }
    }
    if (!variantId) return;

    chain = chain.then(function () {
      return addItems([{ id: variantId, quantity: 1, properties: props }]);
    }).then(function () {
      return withTimeout(refreshDrawer(), REFRESH_TIMEOUT_MS, 'drawer refresh');
    }).then(function () {
      return fetchCart().then(settle);
    }).then(function () {
      reapplySoon();
    }).catch(function (e) { log('gift add back failed', e); });

    return chain;
  }

  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || target.nodeType !== 1 || !target.closest) return;

    var removeBtn = target.closest('[data-s6-gift-remove]');
    if (removeBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleGiftRemove(removeBtn);
      return;
    }
    var addBtn = target.closest('[data-s6-gift-add]');
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleGiftAddBack(addBtn.getAttribute('data-s6-gift-add'), addBtn);
    }
  }, true);

  /* ------------------------------------------- ADD THE GIFT WITH THE PRODUCT */

  function isCartAddUrl(url) {
    return typeof url === 'string' && url.indexOf('/cart/add') !== -1;
  }

  function setDeep(target, path, value) {
    var parts = path.replace(/\]/g, '').split('[');
    var node = target;
    for (var i = 0; i < parts.length - 1; i++) {
      if (typeof node[parts[i]] !== 'object' || node[parts[i]] === null) node[parts[i]] = {};
      node = node[parts[i]];
    }
    node[parts[parts.length - 1]] = value;
  }

  function formStringToObject(str) {
    var out = {};
    String(str).split('&').forEach(function (pair) {
      if (!pair) return;
      var bits = pair.split('=');
      var k = decodeURIComponent(bits[0].replace(/\+/g, ' '));
      var v = decodeURIComponent((bits[1] || '').replace(/\+/g, ' '));
      setDeep(out, k, v);
    });
    return out;
  }

  function readAddItems(data) {
    if (!data) return null;
    if (typeof data === 'string') {
      var head = data.replace(/^\s+/, '').charAt(0);
      if (head === '{' || head === '[') {
        try { return readAddItems(JSON.parse(data)); } catch (e) { return null; }
      }
      return readAddItems(formStringToObject(data));
    }
    if (typeof data !== 'object') return null;
    if (Object.prototype.toString.call(data.items) === '[object Array]') return data.items.slice();
    if (data.items && typeof data.items === 'object') {
      return Object.keys(data.items).sort().map(function (k) { return data.items[k]; });
    }
    if (data.id) {
      var one = { id: data.id, quantity: data.quantity };
      if (data.properties && typeof data.properties === 'object') one.properties = data.properties;
      return [one];
    }
    return null;
  }

  function cleanItem(it) {
    var q = (it.quantity === undefined || it.quantity === null || it.quantity === '') ? 1 : it.quantity;
    var out = { id: it.id, quantity: q };
    if (it.properties && typeof it.properties === 'object' &&
        Object.keys(it.properties).length) out.properties = it.properties;
    return out;
  }

  function cartHasVariant(cart, variantId) {
    var vid = toId(variantId);
    return (cart.items || []).some(function (item) { return toId(item.variant_id) === vid; });
  }

  function giftsForAdd(incomingItems) {
    if (!S6_ENGINE_ENABLED) return [];
    var cart = cachedCart;
    if (!cart || !cart.items) return [];
    var now = Date.now();
    var wanted = {};

    CONFIG.rules.forEach(function (rule) {
      if (!ruleLive(rule, now) || !rule.gift) return;
      var hit = false;
      (cart.items || []).forEach(function (item) {
        if (hit || isAnyGiftLine(item) || item.price === 0) return;
        if (!ruleAllowsLine(rule, item.properties)) return;
        if (ruleHits(rule, toId(item.product_id), toId(item.variant_id))) hit = true;
      });
      incomingItems.forEach(function (it) {
        if (hit) return;
        if (ruleHits(rule, null, toId(it.id)) && ruleAllowsLine(rule, it.properties)) hit = true;
      });
      if (hit) wanted[giftKey(rule.gift.variantId)] = true;
    });

    var out = [];
    Object.keys(wanted).forEach(function (key) {
      if (isDismissed(key)) return;
      var g = giftForKey(key);
      if (!g) return;
      if (cartHasVariant(cart, g.variantId)) return;
      var already = incomingItems.some(function (it) { return toId(it.id) === toId(g.variantId); });
      if (already) return;
      out.push(giftItem(key));
    });
    return out;
  }

  function rewriteAddPayload(original, items, extra) {
    var merged = items.concat(extra).map(cleanItem);
    if (typeof original === 'string') {
      var head = original.replace(/^\s+/, '').charAt(0);
      if (head === '{' || head === '[') return JSON.stringify({ items: merged });
      return merged.map(function (it, i) {
        var parts = ['items[' + i + '][id]=' + encodeURIComponent(it.id),
                     'items[' + i + '][quantity]=' + encodeURIComponent(it.quantity || 1)];
        var props = it.properties || {};
        Object.keys(props).forEach(function (p) {
          parts.push('items[' + i + '][properties][' + encodeURIComponent(p) + ']=' +
                     encodeURIComponent(props[p]));
        });
        return parts.join('&');
      }).join('&');
    }
    return { items: merged };
  }

  function maybeAppendGifts(original) {
    if (s6OwnWrite) return null;
    if (typeof FormData !== 'undefined' && original instanceof FormData) return null;
    var items = readAddItems(original);
    if (!items || !items.length) return null;
    var extra = giftsForAdd(items);
    if (!extra.length) return null;
    log('adding', extra.length, 'gift(s) in the same request as the product');
    return rewriteAddPayload(original, items, extra);
  }

  if (window.jQuery && window.jQuery.ajaxPrefilter) {
    window.jQuery.ajaxPrefilter(function (options) {
      try {
        if (!isCartAddUrl(options.url)) return;
        var rewritten = maybeAppendGifts(options.data);
        if (rewritten !== null) options.data = rewritten;
      } catch (e) { log('prefilter error', e); }
    });
  }

  /* -------------------------------------------------------------- TRIGGERS */

  var scheduled = null;
  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(function () { reconcile(); }, 150);
  }

  ['cart:updated', 'cart:refresh'].forEach(function (name) {
    var handler = function () {
      reapplySoon();
      schedule();
    };
    document.documentElement.addEventListener(name, handler);
    document.addEventListener(name, handler);
  });

  // Every cart write made by any script (custom.js, GoKwik, an app) schedules
  // a pass. Only this file's own writes are skipped, and only the write
  // itself - the pass that follows still reads the cart back.
  if (window.fetch) {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) { url = ''; }

      var mine = s6OwnWrite;
      var args = arguments;

      if (!mine && isCartAddUrl(url) && init && init.body) {
        try {
          var rewritten = maybeAppendGifts(init.body);
          if (rewritten !== null) {
            var copy = {};
            for (var k in init) { if (Object.prototype.hasOwnProperty.call(init, k)) copy[k] = init[k]; }
            copy.body = rewritten;
            args = [input, copy];
          }
        } catch (e) { log('fetch rewrite error', e); }
      }

      var result = nativeFetch.apply(this, args);
      if (!mine && /\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        result.then(function () { schedule(); }).catch(function () {});
      }
      return result;
    };
  }

  if (window.jQuery) {
    window.jQuery(document).ajaxComplete(function (event, xhr, settings) {
      if (!settings || !settings.url) return;
      if (/\/cart\/(add|change|update|clear)(\.js)?/.test(settings.url)) schedule();
    });
  }

  // Coming back to a tab / to the page after checkout was abandoned.
  window.addEventListener('pageshow', function () { schedule(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') schedule();
  });

  /* ----------------------------------------------------------------- BOOT  */

  var watchTries = 0;
  function watchForRerenders() {
    if (!window.MutationObserver) return;
    var targets = [];
    var drawer = document.getElementById('mini-cart');
    if (drawer) targets.push(drawer);
    if (isCartPage()) {
      var main = document.getElementById('main') || document.body;
      if (main) targets.push(main);
    }
    if (!targets.length) {
      if (watchTries++ < 40) setTimeout(watchForRerenders, 250);
      return;
    }
    targets.forEach(function (node) {
      if (node.__s6Watched) return;
      node.__s6Watched = true;
      var queued = false;
      var observer = new MutationObserver(function (records) {
        if (queued || !lastCart) return;
        // Ignore mutations we cause ourselves (pill/bin/toast insertions).
        var external = false;
        for (var i = 0; i < records.length && !external; i++) {
          var r = records[i];
          var t = r.target;
          if (t && t.closest && (t.closest('.s6-runtime-gift') || t.closest('.s6-giftback') || t.closest('.s6-toast'))) continue;
          external = true;
        }
        if (!external) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          reapplyDecorations();
        });
      });
      observer.observe(node, { childList: true, subtree: true });
    });
  }

  function boot() {
    if (!S6_ENGINE_ENABLED) {
      log('disabled in Theme settings > Supply6 Freebies; doing nothing');
      return;
    }
    injectStyles();
    if (!patchMilestoneEngine()) {
      var tries = 0;
      var t = setInterval(function () {
        if (patchMilestoneEngine() || ++tries > 20) clearInterval(t);
      }, 100);
    }
    watchForRerenders();
    reconcile();
  }

  window.S6Freebies = {
    version: '2.1.0',
    enabled: S6_ENGINE_ENABLED,
    config: CONFIG,
    rules: function () { return CONFIG.rules; },
    gifts: giftsByKey,
    families: function () { return CONFIG.familyKeywords.slice(); },
    reconcile: function () { return reconcile(); },
    plan: planFor,
    activeRules: activeRuleIds,
    desired: desiredGifts,
    covered: function (cart) { return coveredFamilies(activeRuleIds(cart || cachedCart || { items: [] })); },
    allowed: function (cart) { return checkoutAllowed(cart || cachedCart); },
    allowsLine: ruleAllowsLine,
    parseTag: parseTag,
    paid: function (cart) { return hasPaidLine(cart || cachedCart); },
    dismissed: readDismissed,
    undismissAll: function () { writeDismissed({}); return reconcile(); },
    offers: function () { return lastCart ? giftBackOffers(lastCart, lastEarned, lastCovered) : []; },
    milestones: milestoneGifts,
    redraw: reapplyDecorations,
    coAdd: giftsForAdd,
    cart: function () { return cachedCart; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
